/**
 * Wordings of «Учитель» v3 — family «endings» (docs/TEACHING-V3.md §2.9, §5; pool specs: ../spec.ts).
 * Rules: no Latin, NO squares (the board shows them), Гамбитик speaks of himself as a boy, the child through {g:…},
 * no «молодец / умница / так держать», no clock words; «вот эти клетки / этот фланг / сюда» only in pools with a board cue.
 *
 * `v3.end.*` — ONE sentence that opens the end of the game: the feelings (joy / support) live HERE, never «молодец»; it
 * is generic about HOW the game ended (a win may be a mate, a resignation or the clock; a draw may be a stalemate).
 *
 * `v3.takeaway.*` — the LESSON of the game to carry into the next one, NOT praise (the opener already did the feelings):
 * a concrete rule or habit + WHY, 1–2 short sentences (≤ 14 words each). It is also shown alone on the result card, so it
 * reads without the opener and stays true after a win, a loss or a draw.
 *  - `mistake.*` — «В следующий раз / На будущее: <правило> — <зачем>». A fact about the game only as «был / мог»,
 *    never «ты проиграл из-за…», no reproach («ты не», «забыл», «зря», «опять», «снова»). Same idea as the in-game
 *    rule (`v3.rule.*`) of that concept, fresh words.
 *  - `theme.*` — the fact that the theme worked (never «поэтому выиграли»; no claim about the king's safety NOW — a theme
 *    takeaway may follow a lost game) + what to keep doing and why. The engine proves only «two goals of the card done»,
 *    so the fact stays soft («план с центром удался»), the habit carries the lesson.
 *  - `found.*` — what the child found (the motif is unknown: «сильный ход / удар / подарок», a mate is a mate) + HOW such
 *    moves are found, to keep doing it. The find is the child's own move, but an arrow may have pointed elsewhere and at
 *    stages 1–2 the target of a treasure is lit — so no «без стрелки / без подсказки», only «сам», «своими глазами».
 *    No «зоркие глаза / какая внимательность / это здорово» — that is praise, not a lesson.
 *  - `quiz.*` — the kind of question the child answered well + the habit behind the question and why it matters.
 *  - `stage.*` — the stage's advice as a rule + why.
 * The child's gender goes through {g:…} and never as «ты …л» (the lint reads the expanded text, so «ты {g:нашёл|нашла}»
 * is flagged too — write «{g:Сам|Сама} {g:нашёл|нашла}» or an impersonal form).
 */
import type { LessonWording, LessonWordings } from '../types.ts';

/** the younger band (stages 1–2) of a banded pool */
const y = (t: string): LessonWording => ({ t, stages: [1, 2] });
/** the older band (stages 3–5) of a banded pool, and the words of stages 3+ (связка, фланг, открытая линия…) */
const o = (t: string): LessonWording => ({ t, stages: [3, 5] });
/** stages 2–5 (развитие, ранний ферзь, слабая пешка) */
const from2 = (t: string): LessonWording => ({ t, stages: [2, 5] });
/** stages 4–5 (защитник, вскрыть, последняя горизонталь) */
const from4 = (t: string): LessonWording => ({ t, stages: [4, 5] });
/** shared by every stage of the pool */
const a = (t: string): LessonWording => ({ t });

export const WORDINGS: LessonWordings = {
  // ───────────────────────── the opener of the game end (one sentence, the feelings) ─────────────────────────

  'v3.end.win': [
    a('Ура, победа — какая получилась партия!'),
    a('Победа — я так рад, что прямо подпрыгиваю!'),
    a('Есть победа — давай пять!'),
    a('Ого, мы выиграли — радость-то какая!'),
    a('Эта партия твоя — поздравляю от всей души!'),
    a('Вот и победа — ух, как было интересно!'),
    a('Выиграли — а я болел за тебя всю партию!'),
    a('Ух ты, мы победили!'),
    a('Ура-ура, сегодня на доске праздник!'),
    a('Партия выиграна, давай порадуемся вместе!'),
    a('Победа за тобой — я улыбаюсь до ушей!'),
    a('Наша взяла — и ещё как!'),
    a('Вот так финал — победа наша!'),
  ],

  'v3.end.loss': [
    a('Эта партия досталась сопернику — ничего, так бывает у всех.'),
    a('Обидно, я понимаю — давай посмотрим, чему нас научила эта партия.'),
    a('Победил соперник — зато мы стали чуточку опытнее.'),
    a('Проигрывают все, даже чемпионы мира, — и каждый раз учатся.'),
    a('Не беда — из проигранных партий получаются самые важные уроки.'),
    a('Эх, в этот раз не получилось — но я в тебя очень верю.'),
    a('Трудная вышла партия — я рядом, разберём её вместе.'),
    a('Грустно проигрывать, это правда — но грусть пройдёт, а умение останется.'),
    a('Я тоже иногда проигрываю — и каждый раз узнаю что-то новое.'),
    a('Выше нос — одна партия ничего не решает!'),
    a('Давай обнимемся — партия была непростая.'),
    a('Проигрыш — это не конец, а подсказка, что учить дальше.'),
    a('Соперник сегодня выиграл — но следующая партия будет совсем новой!'),
  ],

  'v3.end.draw': [
    a('Ничья — никто не победил, и никто не проиграл.'),
    a('Победы нет, поражения нет — а польза есть!'),
    a('Силы оказались равны — ничья.'),
    a('Мир на доске — тоже хороший результат!'),
    a('Разошлись миром — у каждого по половинке победы.'),
    a('Ух, ничья — шахматы умеют удивлять!'),
    a('Партия закончилась вничью — давай посмотрим, что из неё запомнить.'),
    a('Никому не досталась победа — бывает и так!'),
    a('Счёт ровный, никто не обижен!'),
    a('Ничья — значит, кто сильнее, выясним в следующей партии!'),
  ],

  'v3.end.unfinished': [
    a('Партия осталась недоигранной — ничего, сыграем ещё.'),
    a('Эту партию мы не закончили — но кое-что полезное уже видно.'),
    a('Остановились раньше конца — так тоже бывает.'),
    a('Не доиграли — не страшно, доска подождёт.'),
    a('Отдохнём — а шахматы никуда не денутся.'),
    a('Фигуры разошлись по домам раньше времени — вернёмся к шахматам, когда захочешь.'),
    a('Партию прервали — но главное я скажу уже сейчас.'),
    a('До конца не дошли — а кое-чему научиться успели.'),
  ],

  // ───────────────────────── takeaways: a rule for next time + why (never a reproach) ─────────────────────────

  'v3.takeaway.mistake.mate': [
    a('В следующий раз перед ходом спроси себя: что задумал соперник? Так мат не подкрадётся незаметно.'),
    a('На будущее: сначала проверяем, не грозит ли мат нам, потом нападаем сами. Мат решает всё за один ход.'),
    a('Даже в атаке сначала оглянись на своего короля. Если ему поставят мат, наша атака уже не поможет.'),
    a('Мат часто приходит оттуда, куда мы не смотрим. Поэтому перед ходом проверяй все шахи соперника.'),
    y('Правило на будущее: сначала смотрим на ходы соперника, потом ходим сами. Так король будет в безопасности.'),
    y('Королю нужен охранник — это ты! Каждый ход проверяй, спокойно ли ему: мат сразу заканчивает партию.'),
    y('Мат можно заметить заранее: посмотри, какие шахи есть у соперника. Любой мат начинается с шаха.'),
    y('В следующей партии перед ходом поищем шахи соперника. Где шах, там может прятаться и мат.'),
    o('Перед каждым ходом ищи шахи и взятия соперника — так мат не застанет врасплох.'),
    o('Перед ходом сыграй в уме за соперника: есть ли у него мат? Один пропущенный мат решает всю партию.'),
    o('Опаснее всего мат, которого не ждёшь. Поэтому проверяй все шахи соперника заранее.'),
    o('Сначала думай за соперника: его шахи, взятия и угрозы. Так мат видно заранее, а не когда поздно.'),
    o('Шахматы — игра двоих. Прежде чем сходить, загляни в планы соперника: нет ли там мата?'),
  ],

  'v3.takeaway.mistake.backRank': [
    a('На будущее: спрятанному королю нужна форточка. Без неё ладья или ферзь могут поставить мат по последнему ряду.'),
    a('Король, запертый своими пешками, — лёгкая добыча. Сделай ему форточку заранее, пока спокойно.'),
    a('Когда король спрятался, пусть одна пешка перед ним шагнёт вперёд. Так у короля появится выход, если придёт шах.'),
    a('Последний ряд опасен, если королю некуда шагнуть. Одна пешка вперёд — и выход есть.'),
    a('Запомни про форточку: с ней мат по последнему ряду почти не страшен.'),
    a('В следующий раз построй королю домик с дверцей. Запертый король не спасётся от шаха по последнему ряду.'),
    a('Своими пешками король может сам себя запереть. Проверяй, есть ли ему куда шагнуть при шахе.'),
    from4('Мат по последней горизонтали ловит запертого короля. Поэтому заранее сделай ему форточку.'),
  ],

  'v3.takeaway.mistake.fork': [
    a('Один прыжок коня может напасть сразу на две фигуры. Поэтому следи, куда могут прыгнуть кони соперника.'),
    a('В следующий раз представь ответ соперника: не нападёт ли он сразу на двоих? От такого удара спасёшь только одну фигуру.'),
    a('Держи фигуры под защитой — тогда удар сразу по двум не так страшен.'),
    a('Сегодня у соперника был удар сразу по двум фигурам. Ищи такие удары заранее: потом спасти удастся только одну.'),
    y('Чужой конь — большой хитрец: прыгает и нападает сразу на двоих. Следи за ним перед каждым ходом.'),
    y('Когда нападают сразу на две фигуры, спасти можно только одну. Поэтому такие удары ищем заранее.'),
    y('Видишь, что соперник может ударить сразу по двоим? Уведи одну фигуру заранее, пока удара ещё нет.'),
    y('Считай прыжки чужого коня перед ходом: куда он скачет, там и опасно.'),
    o('Вилка — любимый удар коня, и от неё спасёшь только одну фигуру. Проверяй прыжки коней соперника заранее.'),
    o('Перед ходом ищи чужие вилки: не встанут ли две наши фигуры под один удар? До вилки всё ещё можно исправить.'),
    o('Вилку умеет делать любая фигура, даже пешка. Поэтому смотри на все ходы соперника, а не только на коней.'),
    o('Против вилок есть простое средство: думать за соперника до своего хода. Тогда две наши фигуры не встанут под один удар.'),
    o('Вилка с шахом — самая опасная: король уходит, а вторую фигуру забирают. Проверяй, где соперник может дать шах.'),
  ],

  'v3.takeaway.mistake.pin': [
    a('В следующий раз не ставь фигуру между своим королём и чужим слоном или ладьёй. Такая фигура застревает на месте.'),
    a('Фигура, за которой прячется король, может оказаться приклеенной к месту. Поэтому проверяй линии к королю заранее.'),
    a('Чужие слоны, ладьи и ферзь бьют издалека. Проверяй, куда смотрят их линии, чтобы наши фигуры не застряли.'),
    a('Король и ферзь на одной линии — приманка для чужого слона или ладьи. Разводи их по разным линиям.'),
    a('Смотри не только на нашу фигуру, но и на того, кто стоит за ней. Если там король, уходить ей нельзя.'),
    a('Проверяй, не смотрит ли чужой слон или ладья через нашу фигуру на короля. Тогда она не сможет уйти.'),
    o('Связка держит фигуру на месте. В следующий раз не ставь фигуры на одну линию с королём.'),
    o('Связанная фигура — почти пленник. Поэтому заранее уводи короля и ферзя с опасных линий.'),
    o('Связка хитрая: фигура вроде стоит спокойно, а уйти не может. Замечай опасные линии до своего хода.'),
  ],

  'v3.takeaway.mistake.hanging': [
    a('Главное правило на будущее: каждую нашу фигуру кто-то прикрывает. Одинокую фигуру соперник заберёт первой.'),
    a('Сегодня одна наша фигура оказалась в опасности. В следующий раз проверяй каждую перед ходом, и соперник останется без подарков.'),
    a('Прежде чем сходить, спроси себя: кого соперник может съесть? Так фигуры не пропадают просто так.'),
    a('Запомним волшебный вопрос перед ходом: кто защищает мою фигуру? С ним фигуры целее.'),
    a('Незащищённая фигура — угощение для соперника. Перед ходом проверь, кого мы оставили без охраны.'),
    y('Фигуры не любят оставаться одни: одинокую легко съесть. Прежде чем сходить, посмотри, кто их защищает.'),
    y('В следующий раз проверь: не останется ли кто-то из наших без защиты? Такую фигуру соперник съест бесплатно.'),
    y('Наши фигуры — одна команда, пусть прикрывают друг друга. Тогда соперник ничего не съест даром.'),
    y('Сначала проверка, потом ход: всё ли у нас защищено? Проверка занимает миг, а фигура остаётся целой.'),
    y('Одинокая фигура — лёгкая добыча. Перед ходом найди, кто остался один, и позови ему помощника.'),
    o('Перед ходом пройдись взглядом по своим фигурам: кто под ударом и кто его прикрывает? Так ни одна не повиснет.'),
    o('Защита не всегда спасает: если напала фигура дешевле, размен будет невыгодным. Такую фигуру уводи сразу.'),
    o('Хорошая привычка: до хода представь позицию после него — что сможет съесть соперник? Так потери видно заранее.'),
    o('Висячая фигура — первая цель соперника. Ищи такие у себя до того, как сходить.'),
    o('Защита — это счёт: тех, кто нападает, не должно быть больше тех, кто защищает. Иначе фигуру заберут.'),
  ],

  'v3.takeaway.mistake.badTrade': [
    a('Размен — как обмен игрушками: невыгодно отдавать большую за маленькую. Сначала сравни цену, потом меняйся.'),
    a('На будущее: считай фигуры до размена, а не после. После размена уже ничего не вернёшь.'),
    a('Прежде чем съесть защищённую фигуру, посчитай: кто съест в ответ и что останется. Так размен не станет потерей.'),
    a('Ладья дороже коня, а ферзь дороже ладьи. Помни цену фигур, когда меняешься, чтобы не остаться с меньшим.'),
    a('В следующий раз, прежде чем меняться, спроси себя: кто в выигрыше — мы или соперник? Меняемся, только если не теряем.'),
    a('Коня за пешку не отдают: конь стоит целых три пешки. Отдашь — и армия станет слабее.'),
    a('У каждой фигуры своя цена. Перед разменом сравни цены, как в магазине, — и не переплачивай.'),
  ],

  'v3.takeaway.mistake.ignoredDanger': [
    a('Когда соперник напал на нашу фигуру, сначала думаем о ней. Меньше фигур — меньше сил для атаки.'),
    a('Фигуру под ударом можно спасти тремя способами: увести, защитить или закрыть. Выбирай один сразу, пока не поздно.'),
    a('Сначала спасаем, потом играем дальше — так наша армия останется большой.'),
    a('Если фигура в беде, ей нужна помощь прямо сейчас. Через ход её уже могут съесть.'),
    y('Когда я говорю «под ударом», первым делом спасай фигуру. Такое предупреждение помогает её сохранить.'),
    y('Фигура в опасности — как котёнок на дереве: сначала спасаем её! Все остальные дела подождут.'),
    y('В следующий раз, если соперник напал, первым делом выручай свою фигуру. Каждая фигура нам ещё пригодится.'),
    y('Напали на фигуру? Уведи её или позови на помощь другую — так она останется в игре.'),
    o('Угроза, оставленная без ответа, обычно стоит фигуры. Перед ходом проверь: спасён ли тот, на кого напали?'),
    o('Каждый ход соперника — вопрос: на кого он напал? Сначала найди ответ, потом строй свой план.'),
    o('Если фигура под ударом, ищи ход, который её спасает, — или ход ещё сильнее. Просто так фигуры не отдают.'),
    o('Предупреждение об угрозе — подсказка. Используй её, и фигура останется целой.'),
    o('Сильные шахматисты не оставляют угрозы без ответа — даже ради красивого плана. План без фигур не работает.'),
  ],

  'v3.takeaway.mistake.missedTreasure': [
    a('Сегодня на доске ждали подарки. Ищи их перед каждым ходом: лишняя фигура — большая сила.'),
    a('Прежде чем ходить, осмотри доску, как кладоискатель: нет ли где бесплатной фигуры? Каждая такая делает нашу армию сильнее.'),
    a('Запомни вопрос охотника за подарками: кого у соперника можно съесть? Задавай его каждый ход, и подарки не убегут.'),
    a('Подарки на доске надо забирать, пока соперник их не спрятал. В следующий раз проверяй все взятия.'),
    y('В следующий раз перед ходом поищи: кто у соперника стоит без защиты? Такую фигуру можно съесть бесплатно.'),
    y('Включай глаза-фонарики: перед ходом свети на каждую фигуру соперника. Так подарок не спрячется.'),
    y('Перед ходом пересчитай фигуры соперника: у каждой есть охрана? Где охраны нет, там подарок.'),
    y('Соперник иногда оставляет фигуры без защиты — это наш шанс. Проверяй это каждый ход, ведь подарок ждёт недолго.'),
    o('Подарки не кричат о себе — их надо искать каждый ход. Первым делом смотри на взятия.'),
    o('Хорошая привычка: сначала смотри на шахи и взятия, потом на тихие ходы. Так сильные ходы не проходят мимо.'),
    o('Выгодное взятие легко пропустить, если не искать его специально. Поэтому каждый ход начинай с поиска взятий.'),
    o('В следующий раз посчитай фигуры соперника под ударом — одна может оказаться подарком.'),
    o('Сильный игрок каждый ход спрашивает: что мне подарили? Лишняя фигура часто решает партию.'),
  ],

  'v3.takeaway.mistake.earlyQueen': [
    a('Ферзь — главная сила, его берегут. Сначала в бой идут кони и слоны.'),
    a('В следующий раз выводи сначала коней и слонов, а ферзя держи в запасе. Рано вышедшего ферзя гоняют.'),
    a('Ферзя, который вышел рано, гоняют — и он тратит ходы на побег. Поэтому сначала выводим коней и слонов.'),
    a('Ферзю рано на прогулку: пока он бегает, соперник выводит фигуры. Пусть ферзь подождёт дома.'),
    a('Запомним порядок выхода: пешка в центр, кони, слоны, а ферзь — позже. Так все фигуры успевают выйти.'),
    a('Ферзь — как капитан: он выходит, когда команда уже в игре. Одного капитана легко загнать.'),
    from2('Ранний ферзь бегает от чужих фигур, а наши кони всё ещё спят. Сначала будим коней и слонов.'),
    from2('Ранний ферзь — частая ловушка в начале партии. Пусть он выйдет чуть позже, когда фигуры уже в игре.'),
  ],

  // the opponent's tactics: at stages 1–2 only a trapped piece leads here (core RULE_OF), the rest are stage-3+ concepts
  'v3.takeaway.mistake.tactic': [
    a('Сегодня у соперника был хитрый удар. Перед ходом смотри, что он задумал, — тогда такие удары видно заранее.'),
    a('Удар может прийти издалека. Смотри на всю доску, а не только туда, где идёт бой.'),
    a('Подумай за соперника до своего хода — так хитрые удары станут видны заранее.'),
    y('Перед ходом проверь, есть ли у нашей фигуры выход, если на неё нападут. Фигуру без выхода легко поймать.'),
    y('Уходя далеко вперёд, фигура должна знать дорогу назад. Иначе её окружат и поймают.'),
    y('Фигура в чужом лагере может попасть в ловушку. Прежде чем идти туда, найди ей запасной путь.'),
    o('Бывает, соперник отводит одну фигуру, а нападает другая, что стояла за ней. Смотри, что откроется после его хода.'),
    o('Хитрые удары прячутся на длинных линиях. Следи за слонами, ладьями и ферзём соперника.'),
    o('Соперник может съесть того, кто охраняет нашу фигуру, — и она останется одна. Проверяй, на ком держится защита.'),
    o('Сквозной удар прячется на линиях. Не ставь дорогие фигуры одну за другой — задняя может пропасть.'),
    from4('Убрать защитника, вскрыть линию — соперник тоже знает такие приёмы. Будем замечать их заранее, до своего хода.'),
  ],

  // ───────────────────────── takeaways: the theme worked (a fact, never «поэтому выиграли») + what to keep doing ─────────────────────────

  'v3.takeaway.theme.center': [
    a('Сегодня мы заняли центр — как и задумали. Занимай его и дальше: из центра фигуры достают всю доску.'),
    a('Наша тема сработала: центр был за нами. Держи центр в каждой партии — там фигурам просторно.'),
    a('Центр — главные дороги доски, и сегодня они были наши. Спеши туда в каждой партии.'),
    a('Когда центр наш, фигурам просторно, — так у нас сегодня и было. Начинай с центра и в следующей партии.'),
    a('Центр — как вершина горы: оттуда видно всю доску. Сегодня вершина была нашей — забирайся туда и дальше!'),
    a('План с центром удался. Запомни: кто держит середину доски, тому легче и нападать, и защищаться.'),
    a('Сегодня в центре командовали мы. Пешки и фигуры в центре мешают сопернику — продолжай так играть.'),
  ],

  'v3.takeaway.theme.counterCenter': [
    a('Сегодня мы спорили за центр и не отдали его без боя. Чужой центр можно расшатать — не жди, пока он окрепнет.'),
    a('Задумка сработала: центр соперника не остался спокойным. Бей по нему пешкой сбоку — так он теряет силу.'),
    a('Запомни приём: чужой центр можно расшатать пешкой, которая ударит сбоку. Сегодня этот приём сработал.'),
    a('Соперник хотел хозяйничать в центре, но мы ему не дали. Не отдавай середину доски даром — за неё стоит бороться.'),
    a('Центр соперника сегодня был под прицелом. Держи его так и дальше: соперник занят защитой, а не атакой.'),
    a('Наш план сработал: мы не ждали, а сами пошли в бой за середину доски. Смелый удар по центру — хорошее начало.'),
    a('Центр не обязательно занимать — за него можно бороться. Сегодня так и было, и этот приём пригодится ещё не раз.'),
  ],

  'v3.takeaway.theme.development': [
    a('Мы разбудили все фигуры и увели короля в безопасное место. Начинай так каждую партию — вместе фигуры сильнее.'),
    a('Наш план сработал: вся команда вышла в игру. Каждым ходом буди новую фигуру: спящая фигура не помогает.'),
    a('Когда в игре вся армия, играть веселее, — и сегодня так и было. Выводи всех, прежде чем нападать.'),
    a('Быстро вывести фигуры и спрятать короля — в этой партии это получилось. Повторяй этот порядок: он делает начало партии надёжным.'),
    from2('Развитие удалось: ни одна фигура не проспала начало партии. Так и играй — кто вышел первым, тот первым и атакует.'),
    a('Сегодня фигуры не скучали дома, а король не гулял в центре. Запомни: сначала выводим фигуры, потом нападаем.'),
    a('Фигуры сегодня выходили быстро и дружно, и король успел спрятаться. Не ходи одной фигурой дважды, пока другие спят.'),
  ],

  'v3.takeaway.theme.castle': [
    a('Сегодня король рано ушёл в домик — так и было задумано. Прячь его пораньше и дальше: в домике ему спокойнее.'),
    a('Тема партии удалась: рокировку мы сделали вовремя. Не откладывай её — в центре королю опасно.'),
    a('Король в домике — фигурам спокойнее. В этой партии мы спрятали его пораньше — делай так всегда.'),
    a('Рокировка пораньше — наш сегодняшний план, и он удался. Запомни: король в центре — лёгкая цель.'),
    a('Одна рокировка — два дела: король спрятан, ладья в игре. Сегодня у нас так и вышло — пусть это станет привычкой.'),
    a('Сильные шахматисты прячут короля заранее — и мы сегодня тоже. Когда король спрятан, можно спокойно думать об атаке.'),
    a('Сегодня король вовремя переехал в свой домик. Спрятать короля до начала боя — правило, которое стоит хранить.'),
  ],

  'v3.takeaway.theme.f7': [
    a('План сработал: наши фигуры смотрели на пешку возле чужого короля. Ищи это тонкое место в каждой партии — там атака начинается быстрее.'),
    a('Наша тема сработала: фигуры быстро вышли и нашли цель у чужого короля. Выводи фигуры быстро — тогда цель ещё не успеют прикрыть.'),
    a('Пешка у короля соперника — его тонкое место, и сегодня мы туда целились. Целься туда и дальше, пока король дома.'),
    from2('Слабая пешка у короля — наша цель, и сегодня мы держали её на прицеле. Пока король дома, её защищает только он.'),
    from2('Фигуры вышли быстро и нацелились на слабую пешку — план удался. Сначала выведи фигуры, потом целься: так атака сильнее.'),
    a('Запомни, где у соперника тонкое место: пешка возле короля, пока он дома. Сегодня мы это место не упустили.'),
    a('Быстро вывести фигуры и нацелиться на домик соперника — так мы сегодня и играли. Чем быстрее выходят фигуры, тем раньше начинается атака.'),
    a('Сегодня сопернику пришлось беспокоиться о пешке у своего короля. Заставь соперника защищаться — тогда ему некогда нападать.'),
  ],

  'v3.takeaway.theme.fortress': [
    a('Сегодня наши пешки построили в центре крепость, а фигуры спокойно вышли. Строй такую крепость и дальше: за ней фигурам спокойно.'),
    a('Наша тема сработала: пешки в центре держались дружно и крепко. Держи пешки рядом — одна прикрывает другую.'),
    a('Крепость из пешек в центре — надёжная опора для фигур, и сегодня она стояла. Не спеши её ломать.'),
    a('Сначала крепость, потом фигуры — так мы сегодня и играли. Спокойный порядок бережёт от быстрых атак соперника.'),
    a('Запомним: крепкие пешки в центре не пускают соперника внутрь. Сегодня так и было.'),
    a('Спокойно строить и спокойно выводить фигуры — сегодня это получилось. Не торопись: крепкая позиция — хорошая основа для атаки.'),
    a('Стенка из пешек стояла, а за ней собирались наши фигуры. Двигай пешки из стенки только с умом — пешка назад не ходит.'),
  ],

  'v3.takeaway.theme.gambit': [
    a('Гамбит — почти как моё имя! Главное в нём — быстро выводить фигуры, пока соперник занят пешкой.'),
    a('Иногда пешку отдают нарочно, чтобы фигуры вышли быстрее. Это и есть гамбит — и сегодня он сработал.'),
    a('Запомним гамбит: маленькая жертва ради большого центра. После жертвы не медли — выводи фигуры.'),
    a('Сегодня мы играли смело: ради центра не жалели пешку. Смелый план работает, если фигуры выходят быстро.'),
    a('Задумка гамбита удалась: мы боролись за центр и быстро выводили фигуры. В гамбите каждый ход дорог — не трать его впустую.'),
    a('Предложить пешку ради центра — хитрый план, и сегодня он сработал. Но сначала посчитай, что получишь взамен.'),
    a('Пешка — небольшая цена за центр и быстрые фигуры. Но отдавать больше пешки без расчёта не стоит.'),
  ],

  'v3.takeaway.theme.openFile': [
    a('Сегодня ладья нашла свободную дорогу. Выводи ладьи туда, где пешки не мешают, — оттуда они бьют далеко.'),
    a('Наша тема сработала: ладья заняла свободную линию и оттуда давила. Ищи такие линии для ладей и дальше.'),
    a('Ладьям нужен простор, и в этой партии мы им его дали. Ладья за своими пешками почти не играет.'),
    a('Запомни: ладья любит дорогу, где пешки не мешают. Сегодня она такую нашла — ищи её в каждой партии.'),
    a('Ладья сегодня не скучала в углу — она вышла на свободную линию. Там ладья сильнее, чем дома.'),
    a('Свободная дорога для ладьи — наш сегодняшний план, и он удался. Занимай такую дорогу раньше соперника.'),
    o('Открытая линия — дорога для ладьи, и сегодня мы её заняли. Кто занял линию первым, тот по ней и хозяйничает.'),
    o('Ладья на открытой вертикали — так и было задумано. Оттуда она достаёт до самого лагеря соперника.'),
  ],

  'v3.takeaway.theme.kingsideAttack': [
    a('Сегодня наши фигуры собрались рядом с королём соперника. Для атаки нужно несколько фигур — одна не справится.'),
    a('Наш план сработал: фигуры подобрались к королю соперника. Прежде чем атаковать, приводи к нему побольше фигур.'),
    a('Атака на короля начинается, когда фигуры подходят поближе, и сегодня наши так и сделали. Но сначала пусть свой король спрячется.'),
    a('Запомним, как готовят атаку: фигуры по одной подходят к чужому королю. Спешить не надо — сначала соберём силы.'),
    a('Королю соперника сегодня было неуютно — наши фигуры стояли рядом. Чем больше наших фигур у чужого короля, тем опаснее атака.'),
    a('Сегодня наши фигуры дружно шли в гости к чужому королю. Атака получается, когда фигуры помогают друг другу.'),
    o('План на королевском фланге сработал: наши фигуры пришли к королю соперника. Атакуй там, где у тебя больше сил.'),
  ],

  'v3.takeaway.theme.queensideAttack': [
    a('Сегодня мы играли на стороне ферзя — там наши пешки шли вперёд. Пешки впереди отвоёвывают место для фигур.'),
    a('Тема партии удалась: на стороне ферзя у нас стало больше места. Где у нас больше места, там и играй.'),
    a('Наши пешки на стороне ферзя отвоевали место у соперника. Запомни: пешки идут вперёд вместе, а фигуры помогают сзади.'),
    a('Запомни: игра бывает не только у короля — иногда главное на стороне ферзя. Ищи, на какой стороне у соперника меньше сил.'),
    a('Наши пешки и фигуры сегодня вместе давили на стороне ферзя. Нападай там, где наших фигур больше, чем чужих.'),
    a('Сторона ферзя сегодня была нашим полем, и план сработал. Выбирай сторону для игры заранее — так фигуры знают, куда идти.'),
    o('Игра на ферзевом фланге сработала — там мы были сильнее. Наступай на фланге, где у нас больше пешек.'),
  ],

  // ───────────────────────── takeaways: what the child found + how such moves are found ─────────────────────────

  // always after the child's own mating move
  'v3.takeaway.found.mate': [
    a('{g:Сам|Сама} {g:нашёл|нашла} мат — а находят его так: сначала проверяют все шахи. Начинай с шахов каждый ход.'),
    a('Урок партии: мат прячется среди шахов. Поэтому каждый ход сначала проверяй все свои шахи.'),
    a('Главное в этой партии — мат, и он твой. Ищи так же: шахи, взятия, угрозы — именно в таком порядке.'),
    a('Запомни, как выглядит мат: король под шахом, а спастись нечем. Такие ходы ищи первыми.'),
    a('Каждый ход спрашивай себя: какой шах можно дать? Сегодня среди шахов нашёлся мат.'),
    a('Чтобы ставить мат чаще, ищи шахи, от которых королю соперника некуда уйти.'),
    a('Мат часто ставят вдвоём: одна фигура даёт шах, другая закрывает королю выход. Ищи такие пары фигур.'),
    a('Сильнее мата хода нет — поэтому шахи проверяй раньше всего. Так мат не пройдёт мимо.'),
  ],

  // a tactic or a treasure the child found (the motif is not known to the words)
  'v3.takeaway.found.tactic': [
    a('Главное в партии — твоя находка. Ищи так каждый ход: сначала шахи, потом взятия, потом нападения.'),
    a('{g:Сам|Сама} {g:заметил|заметила} сильный ход. Так и дальше: сначала ищи удар, а тихий ход — потом.'),
    a('Сильные ходы часто прячутся там, где фигура соперника стоит без защиты. Проверяй такие фигуры каждый ход.'),
    a('На доске прятался сильный ход, и он нашёлся. Ищи удары, даже когда кажется, что их нет.'),
    y('Сегодня на доске прятался клад, и он достался нам. Перед каждым ходом спрашивай: что у соперника можно съесть?'),
    y('Твоя находка — самое яркое в этой партии. Не забывай и про короля соперника: шах тоже бывает сильным ходом.'),
    y('Хитрый ход нашёлся — ищи так же и в следующей партии! Начинай с вопроса: что у соперника без защиты?'),
    y('Удар сразу по двум фигурам или бесплатная фигура — такие подарки ищем каждый ход. Сегодня один нашёлся.'),
    o('Главный вывод: удары прячутся там, где фигуры соперника стоят на одной линии. Смотри вдоль линий каждый ход.'),
    o('Тактика нашлась своими силами. Запомни, где она обычно прячется: у фигур без защиты и рядом с королём.'),
    o('Вилки, связки и другие удары любят незащищённые фигуры. Ищи такие фигуры у соперника — и у себя тоже.'),
    o('Найденный удар стоит проверить: что ответит соперник? Тогда удар точно сработает.'),
    o('Удары любят позиции, где у соперника что-то плохо стоит. Каждый ход ищи такое слабое место.'),
  ],

  // ───────────────────────── takeaways: the question the child answered well + the habit behind it ─────────────────────────

  'v3.takeaway.quiz.oppIdea': [
    a('Главное сегодня: угадывать замысел соперника у тебя получалось. Спрашивай «что он задумал?» после каждого его хода.'),
    a('Кто понимает, что задумал соперник, тот успевает ответить вовремя. Задавай себе этот вопрос каждый ход.'),
    a('Угадать план соперника — половина победы. Смотри на каждый его ход и спрашивай: зачем?'),
    a('Вопросы про соперника давались тебе легко — продолжай думать за него. Тогда его угрозы не будут неожиданными.'),
    a('Запомни свой приём: сначала понять, чего хочет соперник, а потом ходить. Так мы не пропускаем его угрозы.'),
    a('Сегодня замыслы соперника не были для тебя секретом. Помни: каждый его ход что-то значит — ищи, что именно.'),
    a('Соперник тоже строит планы. Кто их разгадывает, тот может помешать заранее — делай так каждый ход.'),
    a('Думать за соперника — трудное умение, его тренируют каждый ход. Смотри, куда целится его фигура после хода.'),
  ],

  'v3.takeaway.quiz.whichPiece': [
    a('Урок партии: прежде чем ходить, сравни, какой фигурой лучше. Одну и ту же цель достают разные фигуры.'),
    a('Выбор фигуры — уже половина хода. Спрашивай себя: кому из наших это дело по силам?'),
    a('Хорошая привычка: смотри не на одну фигуру, а на всех, кто может сходить. Так находятся сильные ходы.'),
    a('Сегодня выбор фигуры для хода удавался. Помни: спящая фигура в игре полезнее той, что уже работает.'),
    a('Кони, слоны, пешки — у каждой фигуры своя работа. Перед ходом подумай, чья работа сейчас важнее.'),
    a('Нужная фигура в нужный момент — так и выигрывают. Перед ходом сравни хотя бы две фигуры.'),
  ],

  'v3.takeaway.quiz.canCapture': [
    a('На будущее: перед взятием проверь, кто заберёт в ответ. Так подарок не станет ловушкой.'),
    a('Съесть можно часто, а выгодно — не всегда. Бесплатно — забирай, размен — считай, потеря — не бери.'),
    a('Сегодня взятия считались точно. Помни: если фигуру защищают, сравни, кого отдаёшь и кого забираешь.'),
    a('Прежде чем съесть, посчитай: сколько фигур нападает и сколько защищает. Так фигуры не пропадут даром.'),
    a('Выгодное взятие — то, после которого у нас не меньше сил, чем было. Проверяй это перед каждым взятием.'),
    a('Бесплатно, размен или потеря — эти три случая ты уже различаешь. Решай, какой из них, до того как брать.'),
  ],

  'v3.takeaway.quiz.checkEscape': [
    a('От шаха три спасения: уйти, закрыться или съесть. Перебирай все три — первое не всегда самое надёжное.'),
    a('Главное сегодня: при шахе спокойно ищи выход. Если можно съесть того, кто шахует, — проверь это первым.'),
    a('Шах — не беда, когда знаешь три спасения. Выбирай то, после которого король в безопасности.'),
    a('Сегодня спасения от шахов удавались. Запомни: уходя королём, смотри, чтобы на новом месте не было нового шаха.'),
    a('Шахов меньше, когда король спрятан заранее. Поэтому уводи короля в домик пораньше.'),
    a('Закрыться от шаха можно фигурой, но лучше — той, что защищена. Иначе соперник её просто съест.'),
  ],

  'v3.takeaway.quiz.danger': [
    a('Вывод партии: ты видишь, что у нас под ударом. Смотри так перед каждым ходом — тогда успеешь спасти фигуру.'),
    a('Кто видит опасность заранее, тот успевает спасти фигуры. Проверяй после каждого хода соперника: кого он может съесть?'),
    a('Замечать угрозы заранее — привычка настоящего шахматиста. Продолжай: каждый ход соперника — повод проверить свои фигуры.'),
    a('Опасности сегодня не могли от тебя спрятаться. Запомни: смотри не только на свою атаку, но и на свои фигуры.'),
    a('Сначала свои фигуры, потом чужие: кто у нас под ударом? Так мы не теряем фигуры даром.'),
    a('Главный вывод: ты умеешь видеть, кого может съесть соперник. Делай такую проверку перед каждым ходом, а не только когда я спрашиваю.'),
  ],

  'v3.takeaway.quiz.why': [
    a('Главное сегодня: у каждого хода есть цель, и ты её видишь. Перед своим ходом тоже называй цель — хотя бы про себя.'),
    a('Ты понимаешь не только куда ходить, но и зачем. Если цели у хода нет, поищи другой ход.'),
    a('На вопрос «Зачем?» у тебя были верные ответы. Задавай его себе перед каждым ходом — так ходы не будут случайными.'),
    a('Понимать, зачем делается ход, — это и есть шахматная мысль. Ход без цели — пропущенный шанс.'),
    a('Ходы со смыслом складываются в план. Спрашивай «зачем?» и про ходы соперника — его цели тоже важны.'),
    a('Каждый ход со смыслом — так играют настоящие шахматисты. Запомни: сначала цель, потом ход.'),
  ],

  // ───────────────────────── takeaways: the stage's own advice + why (when nothing else fits) ─────────────────────────

  'v3.takeaway.stage.1': [
    a('Главное для нас сейчас — зоркий глаз: что под ударом и что можно съесть. Кто замечает это первым, тот и выигрывает фигуры.'),
    a('Перед ходом два вопроса: кого могут съесть у нас, кого можем съесть мы? Так фигуры целы, а подарки наши.'),
    a('Глаза-сыщики — наша сила. Каждый ход ищем, что под ударом, чтобы не терять фигуры.'),
    a('Будем тренировать глаз: каждый ход смотрим, кто в опасности и где подарок. Чем чаще смотришь, тем быстрее замечаешь.'),
    a('Шахматист всегда смотрит по сторонам: кто напал, кто без защиты, что можно съесть. Так его трудно застать врасплох.'),
    a('В следующей партии проверим зоркость: всё ли защищено, нет ли подарков? Каждая сбережённая фигура нам ещё пригодится.'),
    a('Хороший шахматист замечает всё: и опасность, и подарок. Поэтому смотрим на всю доску, а не на одну фигуру.'),
    a('Каждый ход — как игра в прятки: ищем, где спряталась опасность и где подарок. Кто ищет, тот и находит.'),
  ],

  'v3.takeaway.stage.2': [
    a('Три правила начала партии: пешку в центр, фигуры в игру, короля в домик. С ними начало получается крепким.'),
    a('Запомни дорожку начала партии: центр, потом фигуры, потом рокировка. Каждый шаг помогает следующему.'),
    a('В следующей партии проверим: быстро ли проснутся фигуры и спрячется король? Кто вывел фигуры первым, тому и атаковать.'),
    a('Центр, развитие, рокировка — три шага хорошего начала. Пропустишь шаг, и соперник этим воспользуется.'),
    a('Хорошее начало — это когда все фигуры вышли, а король в домике. Тогда можно смело начинать атаку.'),
    a('Главное сейчас: занимать центр, будить фигуры и прятать короля в домик. Из центра фигурам видно всю доску.'),
    a('Каждым ходом — новая фигура в игру, а король пусть поскорее прячется. Двумя ходами одной фигурой мы теряем время.'),
    a('Фигура, которая спит дома, не помогает. Будим всех по очереди, прежде чем нападать.'),
  ],

  'v3.takeaway.stage.3': [
    a('Главное сейчас: ищи вилки и связки — у соперника и у себя. С них часто начинается выигрыш фигуры.'),
    a('Вилка и связка — два любимых удара. Ищи их каждый ход, ведь соперник тоже их ищет.'),
    a('Когда фигуры соперника стоят неудобно, ищи вилку или связку. Неудобно — это без защиты или на одной линии.'),
    a('Запомни: вилкой может ударить любая фигура — даже пешка! Поэтому проверяй ходы всех фигур, не только коней.'),
    a('В следующей партии будем охотиться за вилками и связками. Начинай охоту с фигур соперника без защиты.'),
    a('Связанная фигура стоит как приклеенная — нападай на неё! Приклеенной фигуре трудно убежать.'),
    a('Смотри на линии и прыжки коня — там прячутся вилки и связки. Кто их видит, тот выигрывает фигуры.'),
    a('Двойной удар сильнее одиночного: от двух угроз сразу не спастись. Ищи, где он возможен.'),
  ],

  'v3.takeaway.stage.4': [
    a('Наш новый приём: убери защитника — и фигура останется одна. Одинокую фигуру уже можно забрать.'),
    a('Открытые линии — дороги для ладей и ферзя. Занимай их раньше соперника: кто первый, тот и хозяин.'),
    a('Если у фигуры всего один охранник, подумай, как его убрать. Без охраны она станет подарком.'),
    a('В следующей партии ищи две вещи: кого лишить защиты и где свободная линия. Обе ведут к атаке.'),
    a('Запомни два приёма: убрать защитника и захватить открытую линию. Они помогают, когда прямой атаки нет.'),
    a('Сильный ход часто начинается с вопроса: кто кого защищает? Ответ подскажет, куда бить.'),
    a('Ладья на открытой линии и удар по защитнику — наши новые инструменты. Пробуй их в каждой партии.'),
    a('Убери того, кто охраняет, — и охраняемая фигура может стать подарком. Сначала посчитай, чем заплатишь за это.'),
  ],

  'v3.takeaway.stage.5': [
    a('Главное сейчас: считай на три хода вперёд и держи в голове план. Так ходы складываются в одну историю.'),
    a('Сильный игрок думает так: мой ход, ответ соперника, мой следующий ход. Так сюрпризов становится меньше.'),
    a('Каждый ход — шаг плана: спроси себя, куда мы идём. Без плана фигуры просто бродят по доске.'),
    a('Запомни порядок: сначала план, потом расчёт, потом ход. Так решения получаются спокойными и точными.'),
    a('В следующей партии перед каждым решением спросим: что ответит соперник? Ход, у которого есть сильный ответ, лучше не делать.'),
    a('Хороший план — как карта: знаешь, куда ведёшь фигуры. С картой не заблудишься даже в сложной позиции.'),
    a('Мастер сначала решает, чего хочет, а потом считает ходы. Так он не тратит силы на лишние варианты.'),
    a('План и расчёт — два крыла шахматиста. Тренируем оба: с одним крылом далеко не улетишь.'),
  ],
};
